(() => {
  'use strict';

  const config = window.odinBoneBeds;

  if (!config?.qualifyingProducts || !config?.boneBeds) {
    console.warn('Odin Bone Beds: configuration not found.');
    return;
  }

  const GIFT_PROPERTY = '_odin_free_bone_beds';
  const ROOT = window.Shopify?.routes?.root || '/';

  const qualifyingProductIds = new Set(
    config.qualifyingProducts.map((product) => Number(product.productId))
  );

  const boneBedVariantIds = new Set(
    Object.values(config.boneBeds).map(Number)
  );

  let syncing = false;
  let syncAgain = false;

  function isQualifyingShoe(item) {
    return qualifyingProductIds.has(Number(item.product_id));
  }

  function isPromoGift(item) {
    return (
      boneBedVariantIds.has(Number(item.variant_id)) &&
      item.properties &&
      String(item.properties[GIFT_PROPERTY]) === 'true'
    );
  }

  function getShoeSize(item) {
    const values = [
      item.variant_title,
      ...(item.options_with_values || []).map((option) => option.value)
    ].filter(Boolean);

    for (const value of values) {
      const match = String(value).match(/(?:^|\D)(1[0-5]|[6-9])(?:\D|$)/);

      if (match) {
        return match[1];
      }
    }

    return null;
  }

  async function getCart() {
    const response = await fetch(`${ROOT}cart.js`, {
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Could not retrieve cart.');
    }

    return response.json();
  }

  async function addPromoGift(variantId, quantity) {
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: Number(quantity),
            properties: {
              [GIFT_PROPERTY]: 'true'
            }
          }
        ]
      })
    });

    if (!response.ok) {
      throw new Error('Could not add promotional Bone Beds.');
    }
  }

  async function changePromoGift(key, quantity) {
    const response = await fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: String(key),
        quantity: Number(quantity)
      })
    });

    if (!response.ok) {
      throw new Error('Could not update promotional Bone Beds.');
    }
  }

  function calculateRequiredGifts(cart) {
    const required = {};

    cart.items
      .filter(isQualifyingShoe)
      .forEach((shoe) => {
        const size = getShoeSize(shoe);

        if (!size) {
          console.warn(
            'Odin Bone Beds: could not determine shoe size:',
            shoe.product_title,
            shoe.variant_title
          );
          return;
        }

        const giftVariantId = Number(config.boneBeds[size]);

        if (!giftVariantId) {
          console.warn(
            'Odin Bone Beds: no Bone Bed mapping for shoe size:',
            size
          );
          return;
        }

        required[giftVariantId] =
          (required[giftVariantId] || 0) + Number(shoe.quantity);
      });

    return required;
  }

  async function reconcileBoneBeds() {
    if (syncing) {
      syncAgain = true;
      return;
    }

    syncing = true;

    try {
      let cart = await getCart();
      const required = calculateRequiredGifts(cart);

      /*
       * STEP 1:
       * Remove promo Bone Beds that no longer have
       * a corresponding qualifying shoe.
       *
       * Manual Bone Beds are ignored because they do not
       * carry the _odin_free_bone_beds property.
       */
      const promoGifts = cart.items.filter(isPromoGift);

      for (const gift of promoGifts) {
        const variantId = Number(gift.variant_id);
        const requiredQuantity = required[variantId] || 0;

        if (requiredQuantity === 0) {
          await changePromoGift(gift.key, 0);
        }
      }

      /*
       * STEP 2:
       * Fetch the cart again after removals.
       */
      cart = await getCart();

      const freshRequired = calculateRequiredGifts(cart);
      const freshPromoGifts = cart.items.filter(isPromoGift);

      const existingByVariant = {};

      freshPromoGifts.forEach((gift) => {
        const variantId = Number(gift.variant_id);

        if (!existingByVariant[variantId]) {
          existingByVariant[variantId] = [];
        }

        existingByVariant[variantId].push(gift);
      });

      /*
       * STEP 3:
       * Make the quantity of each promotional Bone Bed
       * exactly match the quantity of qualifying shoes
       * requiring that size.
       */
      for (const [variantIdString, requiredQuantity] of Object.entries(freshRequired)) {
        const variantId = Number(variantIdString);
        const giftLines = existingByVariant[variantId] || [];

        const existingQuantity = giftLines.reduce(
          (total, gift) => total + Number(gift.quantity),
          0
        );

        if (existingQuantity < requiredQuantity) {
          await addPromoGift(
            variantId,
            requiredQuantity - existingQuantity
          );
        } else if (existingQuantity > requiredQuantity) {
          let quantityToRemove = existingQuantity - requiredQuantity;

          for (const gift of giftLines) {
            if (quantityToRemove <= 0) {
              break;
            }

            const giftQuantity = Number(gift.quantity);
            const removeFromThisLine = Math.min(
              giftQuantity,
              quantityToRemove
            );

            const newQuantity =
              giftQuantity - removeFromThisLine;

            await changePromoGift(
              gift.key,
              newQuantity
            );

            quantityToRemove -= removeFromThisLine;
          }
        }
      }

      /*
       * STEP 4:
       * Tell Symmetry to redraw the cart drawer using
       * the final Shopify cart state.
       */
     document.querySelectorAll('cart-form[data-ajax-update]').forEach((cartForm) => {
  if (typeof cartForm.refresh === 'function') {
    cartForm.refresh();
  }
});

      console.log('Odin Bone Beds: cart synchronized.');

    } catch (error) {
      console.error('Odin Bone Beds:', error);
    } finally {
      syncing = false;

      if (syncAgain) {
        syncAgain = false;
        reconcileBoneBeds();
      }
    }
  }

  /*
   * SYMMETRY 8.3.2
   *
   * Symmetry fires on:cart:change after its own
   * cart update request succeeds.
   *
   * We wait for that event, reconcile the promotional
   * Bone Beds, then refresh the drawer.
   */
  document.addEventListener('on:cart:change', () => {
    reconcileBoneBeds();
  });

  /*
   * Initial reconciliation handles:
   * - page reloads
   * - direct cart-page visits
   * - carts restored from a previous session
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      reconcileBoneBeds();
    });
  } else {
    reconcileBoneBeds();
  }
})();